import { decode } from './polyline'
import { toApiDateTime } from './dateTimeLocal'

export const VALHALLA_OSM_URL = process.env.REACT_APP_VALHALLA_URL
export const DRIVEPOINTS_BACKEND_URL =
  process.env.REACT_APP_DRIVEPOINTS_BACKEND_URL

export const X_DP_MODE = process.env.REACT_APP_X_DP_MODE || ''

export const buildLocateRequest = (latLng, profile) => {
  let valhalla_profile = profile
  if (profile === 'car') {
    valhalla_profile = 'auto'
  }
  return {
    costing: valhalla_profile,
    locations: [{ lat: latLng.lat, lon: latLng.lng }],
  }
}

export const buildHeightRequest = (latLngs) => {
  const shape = []
  for (const latLng of latLngs) {
    shape.push({ lat: latLng[0], lon: latLng[1] })
  }
  return {
    range: latLngs.length > 1,
    shape,
    id: 'valhalla_height',
  }
}

export const buildDirectionsRequest = ({
  profile,
  activeWaypoints,
  settings,
  dateTime,
}) => {
  let valhalla_profile = profile
  if (profile === 'car') {
    valhalla_profile = 'auto'
  }

  const req = {
    json: {
      costing: valhalla_profile,
      costing_options: {
        [valhalla_profile]: { ...settings.costing },
      },
      exclude_polygons: settings.directions.exclude_polygons,
      locations: makeLocations(activeWaypoints),
      units: 'miles', // 'kilometers' or 'miles'
      alternates: settings.directions.alternates,
      id: 'valhalla_directions',
    },
  }

  const apiDateTime = toApiDateTime(dateTime)
  if (apiDateTime) {
    req.json.date_time = apiDateTime
    // Bidirectional A* supports alternates; timedep forward does not.
    // Valhalla still recosts ETAs with time-dependent / traffic speeds.
    // Only applies to depart_at (1) and current (0); arrive_by still uses timedep reverse.
    if (apiDateTime.type === 0 || apiDateTime.type === 1) {
      req.json.prioritize_bidirectional = true
    }
  }
  return req
}

export const parseDirectionsGeometry = (data) => {
  const coordinates = []

  if (
    !data ||
    !data.trip ||
    !data.trip.legs ||
    !Array.isArray(data.trip.legs)
  ) {
    return coordinates
  }

  for (const feat of data.trip.legs) {
    if (feat && feat.shape) {
      const decoded = decode(feat.shape, 6)
      if (decoded && Array.isArray(decoded)) {
        coordinates.push(...decoded)
      }
    }
  }

  return coordinates
}

export const buildIsochronesRequest = ({
  profile,
  center,
  settings,
  denoise,
  generalize,
  maxRange,
  interval,
}) => {
  let valhalla_profile = profile
  if (profile === 'car') {
    valhalla_profile = 'auto'
  }

  return {
    json: {
      polygons: true,
      denoise,
      generalize,
      show_locations: true,
      costing: valhalla_profile,
      costing_options: {
        [valhalla_profile]: { ...settings.costing },
      },
      contours: makeContours({ maxRange, interval }),
      locations: makeLocations([center]),
      units: 'miles', // 'kilometers' or 'miles'
      id: `valhalla_isochrones_lonlat_${center.displaylnglat.toString()}_range_${maxRange.toString()}_interval_${interval.toString()}`,
    },
  }
}

const makeContours = ({ maxRange, interval }) => {
  let contours = []
  while (maxRange > 0) {
    contours.push({ time: maxRange })
    maxRange -= interval
  }
  contours = contours.reverse()
  return contours
}

const makeLocations = (waypoints) => {
  const locations = []
  for (const [idx, waypoint] of waypoints.entries()) {
    // TODO: there are four types of waypoints: break, break_through, through, and via.
    // Ensure that it is either configurable per waypoint with a default of 'break'
    // Note: we changed the default via to break which means a full stop pickup or dropoff.
    const type = [0, waypoints.length - 1].includes(idx) ? 'break' : 'break' // 'via'
    locations.push({
      lon: waypoint.displaylnglat[0],
      lat: waypoint.displaylnglat[1],
      type: type,
    })
  }

  return locations
}

/**
 * Get the visit order for each waypoint based on trip.locations from optimization results.
 * Returns a map of waypoint index -> visit order (1-based, where 1 is the first stop after start).
 * Returns null if optimization data is not available.
 *
 * @param {Object} tripData - The trip object from route response (trip.locations)
 * @returns {Object|null} - Map of original_index -> visit order, or null if not available
 */
export const getVisitOrder = (tripData) => {
  if (!tripData || !tripData.locations || !Array.isArray(tripData.locations)) {
    return null
  }

  // Check if locations have original_index (indicates optimization was done)
  const hasOptimization = tripData.locations.some(
    (loc) => loc.original_index !== undefined
  )

  if (!hasOptimization) {
    return null
  }

  // Create a map: original_index -> visit order (position in trip.locations array)
  // trip.locations is already in visit order, so the index in the array is the visit order
  const visitOrderMap = {}
  tripData.locations.forEach((location, visitIndex) => {
    if (location.original_index !== undefined) {
      // visitIndex is 0-based, but we want 1-based for display (skip start point)
      // The first location (index 0) is the start, so visit order starts at 1
      visitOrderMap[location.original_index] = visitIndex
    }
  })

  return visitOrderMap
}

export const buildTraceRouteRequest = ({
  profile,
  points,
  settings,
  dateTime,
}) => {
  let valhalla_profile = profile
  if (profile === 'car' || profile === 'autodp') {
    valhalla_profile = 'auto'
  } else if (profile === 'truck' || profile === 'truckdp') {
    valhalla_profile = 'truck'
  }

  // Build shape from points
  const shape = points.map((pt) => ({
    lat: pt.lat,
    lon: pt.lon,
  }))

  const req = {
    costing: valhalla_profile,
    costing_options: {
      [valhalla_profile]: { ...settings.costing },
    },
    shape,
    shape_match: 'walk_or_snap',
    units: 'miles',
    id: 'trace_route',
  }

  if (dateTime && dateTime.type > -1) {
    const apiDateTime = toApiDateTime(dateTime)
    if (apiDateTime) {
      req.date_time = apiDateTime
      if (apiDateTime.type === 0 || apiDateTime.type === 1) {
        req.prioritize_bidirectional = true
      }
    }
  }

  return req
}
